import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  ChevronRight,
  History,
} from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import { useSWRConfig } from 'swr';
import { useOpenChatInCasePartner } from '@/components/case-partner/hooks/useOpenInCasePartner';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import {
  type DraftDocxEditorHandle,
  DraftDocxViewer,
} from '@/components/drafts/DraftDocxViewer';
import PageError from '@/components/shared/Error';
import Loading from '@/components/shared/Loading';
import { fullName } from '@/lib/user';
import { DraftDetailToolbar } from '@/drafting/components/DraftDetailToolbar';
import { DraftHistoryPanel } from '@/drafting/components/DraftHistoryPanel';
import { DraftTitleEditor } from '@/drafting/components/DraftTitleEditor';
import { DraftVersionDiff } from '@/drafting/components/DraftVersionDiff';
import {
  AUTOSAVE_IDLE_DELAY_MS,
  AUTOSAVE_IDLE_GRACE_MS,
  DRAFT_DOCX_ERROR_GRACE_MS,
  DRAFT_LIVE_REFRESH_INTERVAL_MS,
  DRAFT_LOAD_ERROR_GRACE_MS,
  DRAFT_HISTORY_TAB_ID,
} from '@/drafting/constants';
import { useTransientErrorGrace } from '@/drafting/hooks/useTransientErrorGrace';
import type { BrowserIdleCallbackHandle, DraftViewMode, LiveDocumentSeed } from '@/drafting/types';
import { cancelBrowserIdleCallback, scheduleBrowserIdleCallback } from '@/drafting/utils/autosave';
import { uniqueDraftCollaborators } from '@/drafting/utils/collaborators';
import { draftMovedMessage, draftMovedToVersionNumber, saveEditorBuffer } from '@/drafting/utils/editor';
import { formatDateTime, formatRecordedTimestampLabel } from '@/drafting/utils/history';
import { draftCollaborationUserColor, useDraftCollaboration } from '@/drafting/hooks/useDraftCollaboration';
import {
  checkpointDraftDocumentWorkingCopyDocx,
  type DraftDocument,
  downloadDraftDocumentCheckpointDocx,
  downloadDraftDocumentDocx,
  renameDraftDocument,
  restoreDraftDocumentVersion,
  saveDraftDocumentDocx,
  type DraftDocumentVisibility,
  updateDraftDocumentVisibility,
  useDraftDocument,
  useDraftDocumentChangesDocx,
  useDraftDocumentCheckpointDocx,
  useDraftDocumentDocx,
  useDraftDocumentVersion,
  useDraftDocumentVersions,
} from '@/hooks/useDraftDocuments';
import { apiErrorMessage } from '@/lib/api-client';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { resolveDraftDocumentBufferSource } from '@/drafting/utils/draftDocumentBufferSource';
import { getEnvConfig } from '@/lib/env-config';

const DraftDetailPage = () => {
  const { slug, documentGuid } = useParams({ from: '/matter/$slug/drafting/$documentGuid' });
  const {
    version: requestedVersion,
    mode: requestedView,
  } = useSearch({ from: '/matter/$slug/drafting/$documentGuid' });
  const navigate = useNavigate();
  const { user } = useAuth();
  const dateLocale = useMatterLocale();
  const { mutate: mutateDraftDocumentsCache } = useSWRConfig();
  const {
    isOpen: isSidePanelOpen,
    openPanel,
    setTabs,
    tabs: sidePanelTabs,
  } = useSidePanel();
  const openChatInCasePartner = useOpenChatInCasePartner();
  const draftDocumentsListCacheKey = `/matter/${slug}/drafts`;
  const requestedVersionNumber = React.useMemo(() => {
    const parsed = Number(requestedVersion);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [requestedVersion]);
  const requestedViewMode: DraftViewMode = requestedView === 'diff' ? 'diff' : 'docx';
  const collaborationServerUrl = React.useMemo(() => {
    try {
      return getEnvConfig().collaborationServerUrl?.trim() || null;
    } catch {
      return null;
    }
  }, []);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = React.useState(false);
  const [isRecordingVersion, setIsRecordingVersion] = React.useState(false);
  const [selectedVersionNumber, setSelectedVersionNumber] = React.useState<number | null>(requestedVersionNumber);
  const [viewMode, setViewMode] = React.useState<DraftViewMode>('docx');
  const [isDirty, setIsDirty] = React.useState(false);
  const [localLastEditedAt, setLocalLastEditedAt] = React.useState<string | null>(null);
  const [restoringVersionNumber, setRestoringVersionNumber] = React.useState<number | null>(null);
  const [liveDocumentSeed, setLiveDocumentSeed] = React.useState<LiveDocumentSeed | null>(null);
  const [liveCollaborationRoomName, setLiveCollaborationRoomName] = React.useState<string | null>(null);
  const liveEditorHasUnsavedLocalEdits = selectedVersionNumber === null && viewMode === 'docx' && isDirty;
  const liveEditorRefreshInterval = liveEditorHasUnsavedLocalEdits ? 0 : DRAFT_LIVE_REFRESH_INTERVAL_MS;
  const {
    draftDocument,
    isLoading,
    isError,
    isValidating: isDraftDocumentValidating,
    mutate: mutateDraftDocument,
  } = useDraftDocument(
    slug,
    documentGuid,
    { refreshInterval: liveEditorRefreshInterval },
  );
  const editorInstanceKey = 0;
  const editorRef = React.useRef<DraftDocxEditorHandle>(null);
  const isDirtyRef = React.useRef(false);
  const autosaveTimeoutRef = React.useRef<number | null>(null);
  const autosaveIdleCallbackRef = React.useRef<BrowserIdleCallbackHandle | null>(null);
  const autosaveQueuedAfterCheckpointRef = React.useRef(false);
  const editGenerationRef = React.useRef(0);
  const lastLocalEditAtRef = React.useRef(0);
  const scheduleIdleAutosaveRef = React.useRef<((delayMs?: number) => void) | null>(null);
  const checkpointPromiseRef = React.useRef<Promise<string | null> | null>(null);
  const recordVersionInFlightRef = React.useRef(false);
  const latestCheckpointGuidRef = React.useRef<string | null>(null);
  const liveEditorLoadedBufferRoomRef = React.useRef<string | null>(null);
  const lastSeenDraftGuidRef = React.useRef<string | null>(null);
  const lastSeenLatestVersionRef = React.useRef<number | null>(null);

  const {
    versions,
    isLoading: isVersionsLoading,
    mutate: mutateVersions,
  } = useDraftDocumentVersions(slug, documentGuid, { refreshInterval: DRAFT_LIVE_REFRESH_INTERVAL_MS });
  const canWriteDraft = draftDocument?.can_write ?? false;
  const latestHistoryVersion = versions[0] ?? null;
  const summaryLatestVersionNumber = draftDocument?.latest_version?.version_number ?? null;
  const latestHistoryVersionNumber = latestHistoryVersion?.version_number ?? null;
  const latestVersionNumber = Math.max(
    summaryLatestVersionNumber ?? 0,
    latestHistoryVersionNumber ?? 0,
  ) || null;
  const resolvedVersionNumber = selectedVersionNumber ?? latestVersionNumber ?? null;
  const displayVersionNumber = resolvedVersionNumber ?? 1;
  const previousVersionNumber = displayVersionNumber > 1 ? displayVersionNumber - 1 : null;
  const canShowDiff = displayVersionNumber > 1;
  const effectiveViewMode: DraftViewMode = viewMode === 'diff' && canShowDiff ? 'diff' : 'docx';
  const isLiveEditingView = selectedVersionNumber === null && effectiveViewMode === 'docx';
  const draftCollaborationRoomName = draftDocument?.collaboration?.room_name ?? null;
  const latestWorkingCopyCheckpointGuid = draftDocument?.collaboration?.latest_checkpoint_guid ?? null;
  const latchedLiveDocumentSeed = liveDocumentSeed
    && liveDocumentSeed.documentGuid === draftDocument?.guid
    && liveDocumentSeed.versionNumber === latestVersionNumber
    ? liveDocumentSeed
    : null;
  const liveSeedCheckpointGuid = isLiveEditingView
    ? (latchedLiveDocumentSeed?.checkpointGuid ?? latestWorkingCopyCheckpointGuid)
    : null;
  const displayedLiveEditedAt = (
    localLastEditedAt
    ?? draftDocument?.collaboration.last_checkpointed_at
    ?? draftDocument?.updated_at
    ?? draftDocument?.created_at
    ?? null
  );
  // SuperDoc must be told to join or create up front, so seed the guess from the
  // backend's view of the room. A wrong guess is corrected by the editor.
  const draftRoomHasContent = Boolean(
    draftDocument?.collaboration?.status === 'active'
    || draftDocument?.collaboration?.state_vector
    || latestWorkingCopyCheckpointGuid,
  );
  const draftEditingEnabled = Boolean(canWriteDraft && isLiveEditingView);
  const liveDraftEnabled = Boolean(collaborationServerUrl && draftEditingEnabled);
  const isDraftEditingActive = draftEditingEnabled;
  const activeDraftGuid = draftDocument?.guid;
  React.useEffect(() => {
    if (!isLiveEditingView || activeDraftGuid !== documentGuid) {
      setLiveCollaborationRoomName(null);
      return;
    }
    if (!draftCollaborationRoomName) return;
    setLiveCollaborationRoomName((currentRoomName) => (
      currentRoomName === draftCollaborationRoomName ? currentRoomName : draftCollaborationRoomName
    ));
  }, [
    activeDraftGuid,
    documentGuid,
    draftCollaborationRoomName,
    isLiveEditingView,
  ]);
  const {
    version: selectedVersion,
    isLoading: isSelectedVersionLoading,
    isError: selectedVersionError,
    mutate: mutateSelectedVersion,
  } = useDraftDocumentVersion(slug, documentGuid, resolvedVersionNumber);
  const {
    version: previousVersion,
    isLoading: isPreviousVersionLoading,
    isError: previousVersionError,
  } = useDraftDocumentVersion(slug, documentGuid, viewMode === 'diff' ? previousVersionNumber : null);
  const {
    documentBuffer: changesDocumentBuffer,
    isLoading: isChangesDocxLoading,
    isError: changesDocxError,
  } = useDraftDocumentChangesDocx(slug, documentGuid, viewMode === 'diff' ? displayVersionNumber : null);
  const {
    documentBuffer: versionDocumentBuffer,
    isLoading: isDocxLoading,
    isValidating: isDocxValidating,
    isError: docxError,
    mutate: mutateDocx,
  } = useDraftDocumentDocx(
    slug,
    documentGuid,
    resolvedVersionNumber,
    { normalizeForPreview: !isDraftEditingActive },
  );
  const {
    documentBuffer: liveCheckpointBuffer,
    isLoading: isLiveCheckpointLoading,
    isValidating: isLiveCheckpointValidating,
    isError: liveCheckpointError,
  } = useDraftDocumentCheckpointDocx(
    slug,
    documentGuid,
    isLiveEditingView ? liveSeedCheckpointGuid : null,
    { normalizeForPreview: false },
  );
  const liveDocumentSource = resolveDraftDocumentBufferSource({
    checkpoint: {
      buffer: liveCheckpointBuffer,
      error: liveCheckpointError,
      isLoading: isLiveCheckpointLoading,
      isValidating: isLiveCheckpointValidating,
    },
    checkpointGuid: liveSeedCheckpointGuid,
    preferCheckpoint: isLiveEditingView,
    version: {
      buffer: versionDocumentBuffer,
      error: docxError,
      isLoading: isDocxLoading,
      isValidating: isDocxValidating,
    },
    versionNumber: resolvedVersionNumber,
  });
  const liveDocumentBuffer = liveDocumentSource.buffer;
  const collaborationUser = React.useMemo(() => {
    const name = fullName(user?.dbUser) || user?.name || user?.email || 'Crimson user';
    const email = user?.dbUser?.email || user?.email || null;
    return {
      color: draftCollaborationUserColor(email || name),
      email,
      name,
    };
  }, [user?.dbUser, user?.email, user?.name]);
  const draftCollaboration = useDraftCollaboration({
    documentGuid,
    enabled: liveDraftEnabled,
    localUser: collaborationUser,
    matterGuid: slug,
    roomHasContent: draftRoomHasContent,
    roomName: liveCollaborationRoomName ?? draftCollaborationRoomName,
    serverUrl: collaborationServerUrl,
  });
  const activeLiveDraftEnabled = liveDraftEnabled;
  const liveDraftConnectionError = Boolean(draftCollaboration.error);
  const activeLiveRoomName = draftCollaboration.roomName
    ?? liveCollaborationRoomName
    ?? draftCollaborationRoomName
    ?? 'pending';
  // Live sessions are keyed by room, not by working-copy checkpoint. The DOCX
  // buffer only seeds Yjs; later autosaves must not remount SuperDoc.
  const liveEditorReloadKey = activeLiveDraftEnabled
    ? `live-room:${activeLiveRoomName}`
    : liveDocumentSource.sourceKey;
  React.useEffect(() => {
    if (!activeLiveDraftEnabled || !isLiveEditingView || activeDraftGuid !== documentGuid) {
      liveEditorLoadedBufferRoomRef.current = null;
      return;
    }
    if (liveDocumentBuffer) {
      liveEditorLoadedBufferRoomRef.current = activeLiveRoomName;
    }
  }, [
    activeDraftGuid,
    activeLiveRoomName,
    activeLiveDraftEnabled,
    documentGuid,
    isLiveEditingView,
    liveDocumentBuffer,
  ]);
  const liveDraftExportReady = !activeLiveDraftEnabled
    || liveDraftConnectionError
    || (draftCollaboration.ready && draftCollaboration.status === 'connected');
  const liveDraftWaitMessage = (() => {
    if (liveDraftConnectionError) return 'Live draft is offline. Saving this editor locally.';
    if (!draftCollaboration.ready) return 'Live draft is still joining. Try again in a moment.';
    return 'Live draft is reconnecting. Try again in a moment.';
  })();
  const draftDocumentErrorInGrace = useTransientErrorGrace(
    isError,
    Boolean(draftDocument),
    DRAFT_LOAD_ERROR_GRACE_MS,
  );
  const liveDocumentErrorInGrace = useTransientErrorGrace(
    liveDocumentSource.error,
    Boolean(liveDocumentBuffer),
    DRAFT_DOCX_ERROR_GRACE_MS,
  );
  React.useEffect(() => {
    if (!isLiveEditingView || !activeDraftGuid) {
      setLiveDocumentSeed(null);
      return;
    }

    setLiveDocumentSeed((current) => {
      if (
        current?.documentGuid === activeDraftGuid
        && current.versionNumber === latestVersionNumber
      ) {
        return current;
      }

      return {
        checkpointGuid: latestWorkingCopyCheckpointGuid,
        documentGuid: activeDraftGuid,
        versionNumber: latestVersionNumber,
      };
    });
  }, [
    activeDraftGuid,
    isLiveEditingView,
    latestVersionNumber,
    latestWorkingCopyCheckpointGuid,
  ]);
  const clearAutosaveTimeout = React.useCallback(() => {
    if (autosaveTimeoutRef.current !== null) {
      window.clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
    if (autosaveIdleCallbackRef.current !== null) {
      cancelBrowserIdleCallback(autosaveIdleCallbackRef.current);
      autosaveIdleCallbackRef.current = null;
    }
  }, []);

  const setEditorDirty = React.useCallback((dirty: boolean) => {
    if (dirty) editGenerationRef.current += 1;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
    if (dirty) setLocalLastEditedAt(new Date().toISOString());
  }, []);

  React.useEffect(() => {
    if (!documentGuid) return;
    setLocalLastEditedAt(null);
    clearAutosaveTimeout();
  }, [clearAutosaveTimeout, documentGuid]);

  React.useEffect(() => () => {
    clearAutosaveTimeout();
  }, [clearAutosaveTimeout]);

  const checkpointCurrentWorkingCopy = React.useCallback(async (
    checkpointReason: string,
    options: { requireEditor?: boolean; silent?: boolean } = {},
  ): Promise<string | null> => {
    if (!canWriteDraft || !activeDraftGuid || !latestVersionNumber || !draftEditingEnabled) {
      return latestCheckpointGuidRef.current;
    }
    if (recordVersionInFlightRef.current) {
      return latestCheckpointGuidRef.current;
    }
    if (activeLiveDraftEnabled && !liveDraftExportReady) {
      if (options.requireEditor || isDirtyRef.current) {
        throw new Error(liveDraftWaitMessage);
      }
      return latestCheckpointGuidRef.current;
    }
    const editor = editorRef.current;
    if (!editor) {
      if (options.requireEditor || isDirtyRef.current) {
        throw new Error('Draft editor is not ready yet');
      }
      return latestCheckpointGuidRef.current;
    }
    if (checkpointPromiseRef.current) {
      return checkpointPromiseRef.current;
    }

    const checkpointPromise = (async () => {
      const checkpointEditGeneration = editGenerationRef.current;
      const savedBuffer = await saveEditorBuffer(editor);
      if (!savedBuffer) {
        throw new Error('Draft editor did not return a saved document');
      }

      const response = await checkpointDraftDocumentWorkingCopyDocx(
        slug,
        documentGuid,
        latestVersionNumber,
        savedBuffer,
        checkpointReason,
      );
      latestCheckpointGuidRef.current = response.checkpoint.checkpoint_guid;
      if (editGenerationRef.current === checkpointEditGeneration) {
        setEditorDirty(false);
      } else {
        isDirtyRef.current = true;
        setIsDirty(true);
      }
      await mutateDraftDocument(response.document, { revalidate: false });
      return response.checkpoint.checkpoint_guid;
    })();

    checkpointPromiseRef.current = checkpointPromise;
    try {
      return await checkpointPromise;
    } catch (error) {
      if (!options.silent) {
        toast.error(apiErrorMessage(error) || (error instanceof globalThis.Error ? error.message : 'Could not autosave draft'));
      }
      throw error;
    } finally {
      checkpointPromiseRef.current = null;
      if (autosaveQueuedAfterCheckpointRef.current && isDirtyRef.current) {
        autosaveQueuedAfterCheckpointRef.current = false;
        scheduleIdleAutosaveRef.current?.();
      }
    }
  }, [
    activeDraftGuid,
    canWriteDraft,
    documentGuid,
    draftEditingEnabled,
    latestVersionNumber,
    liveDraftExportReady,
    activeLiveDraftEnabled,
    liveDraftWaitMessage,
    mutateDraftDocument,
    setEditorDirty,
    slug,
  ]);

  const scheduleIdleAutosave = React.useCallback((delayMs = AUTOSAVE_IDLE_DELAY_MS) => {
    if (autosaveTimeoutRef.current !== null || autosaveIdleCallbackRef.current !== null) return;
    if (recordVersionInFlightRef.current) return;
    if (viewMode !== 'docx' || !draftEditingEnabled) return;

    autosaveTimeoutRef.current = window.setTimeout(() => {
      autosaveTimeoutRef.current = null;
      if (recordVersionInFlightRef.current) return;
      if (!isDirtyRef.current || viewMode !== 'docx' || !draftEditingEnabled) return;
      if (checkpointPromiseRef.current) {
        autosaveQueuedAfterCheckpointRef.current = true;
        return;
      }

      autosaveIdleCallbackRef.current = scheduleBrowserIdleCallback(() => {
        autosaveIdleCallbackRef.current = null;
        const idleForMs = performance.now() - lastLocalEditAtRef.current;
        if (idleForMs < AUTOSAVE_IDLE_DELAY_MS - AUTOSAVE_IDLE_GRACE_MS) {
          scheduleIdleAutosaveRef.current?.(
            Math.max(AUTOSAVE_IDLE_DELAY_MS - idleForMs, AUTOSAVE_IDLE_GRACE_MS),
          );
          return;
        }
        if (!isDirtyRef.current || viewMode !== 'docx' || !draftEditingEnabled) return;
        if (recordVersionInFlightRef.current) return;
        if (activeLiveDraftEnabled && !liveDraftExportReady) {
          scheduleIdleAutosaveRef.current?.(AUTOSAVE_IDLE_DELAY_MS);
          return;
        }
        if (checkpointPromiseRef.current) {
          autosaveQueuedAfterCheckpointRef.current = true;
          return;
        }

        void checkpointCurrentWorkingCopy('autosave', { silent: true }).catch(() => {
          // Explicit save paths surface errors; idle autosave retries on the next edit.
        });
      });
    }, Math.max(delayMs, 0));
  }, [
    checkpointCurrentWorkingCopy,
    draftEditingEnabled,
    activeLiveDraftEnabled,
    liveDraftExportReady,
    viewMode,
  ]);

  const handleEditorChange = React.useCallback(() => {
    lastLocalEditAtRef.current = performance.now();
    if (isDirtyRef.current) {
      editGenerationRef.current += 1;
    } else {
      setEditorDirty(true);
    }
    scheduleIdleAutosave();
  }, [scheduleIdleAutosave, setEditorDirty]);

  React.useEffect(() => {
    scheduleIdleAutosaveRef.current = scheduleIdleAutosave;
    return () => {
      if (scheduleIdleAutosaveRef.current === scheduleIdleAutosave) {
        scheduleIdleAutosaveRef.current = null;
      }
    };
  }, [scheduleIdleAutosave]);

  React.useEffect(() => {
    setSelectedVersionNumber(requestedVersionNumber);
    setViewMode(requestedViewMode);
  }, [requestedVersionNumber, requestedViewMode]);

  React.useEffect(() => {
    latestCheckpointGuidRef.current = latestWorkingCopyCheckpointGuid;
  }, [latestWorkingCopyCheckpointGuid]);

  React.useEffect(() => {
    if (!isDirty) {
      clearAutosaveTimeout();
      return;
    }

    scheduleIdleAutosave();
  }, [
    clearAutosaveTimeout,
    isDirty,
    scheduleIdleAutosave,
  ]);

  React.useEffect(() => {
    if (!latestHistoryVersionNumber) return;

    if (lastSeenDraftGuidRef.current !== documentGuid) {
      lastSeenDraftGuidRef.current = documentGuid;
      lastSeenLatestVersionRef.current = latestHistoryVersionNumber;
      return;
    }

    const previousLatestVersion = lastSeenLatestVersionRef.current;
    if (previousLatestVersion === null) {
      lastSeenLatestVersionRef.current = latestHistoryVersionNumber;
      return;
    }

    if (latestHistoryVersionNumber <= previousLatestVersion) {
      lastSeenLatestVersionRef.current = latestHistoryVersionNumber;
      return;
    }

    lastSeenLatestVersionRef.current = latestHistoryVersionNumber;
    void mutateDraftDocument();
  }, [
    documentGuid,
    latestHistoryVersionNumber,
    mutateDraftDocument,
  ]);

  const handleRenameDraft = React.useCallback(async (nextTitle: string) => {
    if (!canWriteDraft) {
      toast.error('You only have read access to this draft');
      return;
    }
    const renamedDraft = await renameDraftDocument(slug, documentGuid, nextTitle);
    const applyRenamedDraftFields = (currentDraft: DraftDocument): DraftDocument => ({
      ...currentDraft,
      file_extension: renamedDraft.file_extension,
      file_name: renamedDraft.file_name,
      file_reference: renamedDraft.file_reference,
      last_updated_by: renamedDraft.last_updated_by,
      title: renamedDraft.title,
      updated_at: renamedDraft.updated_at,
    });
    await mutateDraftDocument(
      (currentDraft) => currentDraft
        ? applyRenamedDraftFields(currentDraft)
        : renamedDraft,
      { revalidate: false },
    );
    void mutateDraftDocumentsCache(
      draftDocumentsListCacheKey,
      (current: { documents?: DraftDocument[] } | undefined) => {
        if (!current?.documents) return current;
        return {
          ...current,
          documents: current.documents.map((document) => (
            document.guid === documentGuid
              ? applyRenamedDraftFields(document)
              : document
          )),
        };
      },
      { revalidate: false },
    );
    void mutateSelectedVersion();
  }, [
    canWriteDraft,
    documentGuid,
    draftDocumentsListCacheKey,
    mutateDraftDocument,
    mutateDraftDocumentsCache,
    mutateSelectedVersion,
    slug,
  ]);

  const refreshAfterDraftMoved = React.useCallback(async (movedToVersion: number) => {
    await Promise.all([
      mutateDraftDocument(),
      mutateVersions(),
      mutateSelectedVersion(),
      mutateDocx(),
      mutateDraftDocumentsCache(draftDocumentsListCacheKey),
    ]);
    setSelectedVersionNumber(null);
    setViewMode('docx');
    toast(draftMovedMessage(movedToVersion));
  }, [
    draftDocumentsListCacheKey,
    mutateDocx,
    mutateDraftDocument,
    mutateDraftDocumentsCache,
    mutateSelectedVersion,
    mutateVersions,
  ]);

  const handleVisibilityChange = React.useCallback(async (visibility: DraftDocumentVisibility) => {
    if (!draftDocument?.can_update_visibility || draftDocument.visibility === visibility) return;

    setIsUpdatingVisibility(true);
    try {
      const updatedDraft = await updateDraftDocumentVisibility(slug, documentGuid, visibility);
      await mutateDraftDocument(
        (currentDraft) => currentDraft ? { ...currentDraft, ...updatedDraft } : updatedDraft,
        { revalidate: false },
      );
      void mutateDraftDocumentsCache(
        draftDocumentsListCacheKey,
        (current: { documents?: DraftDocument[] } | undefined) => {
          if (!current?.documents) return current;
          return {
            ...current,
            documents: current.documents.map((document) => (
              document.guid === updatedDraft.guid ? { ...document, ...updatedDraft } : document
            )),
          };
        },
        { revalidate: false },
      );
      toast.success(visibility === 'private' ? 'Draft is now private' : 'Draft is open to the matter');
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to update draft sharing');
    } finally {
      setIsUpdatingVisibility(false);
    }
  }, [
    documentGuid,
    draftDocument,
    draftDocumentsListCacheKey,
    mutateDraftDocument,
    mutateDraftDocumentsCache,
    slug,
  ]);

  const handleDownload = React.useCallback(async () => {
    if (!draftDocument || !resolvedVersionNumber) {
      toast.error('Draft document is missing a version to download');
      return;
    }

    setIsDownloading(true);
    try {
      if (isLiveEditingView && canWriteDraft && draftEditingEnabled) {
        const checkpointGuid = await checkpointCurrentWorkingCopy('manual_download', {
          requireEditor: true,
          silent: true,
        });
        if (!checkpointGuid) {
          throw new Error('Draft checkpoint is not ready to download');
        }
        await downloadDraftDocumentCheckpointDocx(
          slug,
          documentGuid,
          checkpointGuid,
          draftDocument,
        );
        return;
      }

      await downloadDraftDocumentDocx(
        slug,
        documentGuid,
        resolvedVersionNumber,
        draftDocument.title,
      );
    } catch (error) {
      const movedToVersion = draftMovedToVersionNumber(error);
      if (movedToVersion) {
        await refreshAfterDraftMoved(movedToVersion);
        return;
      }
      toast.error(apiErrorMessage(error) || 'Failed to download draft document');
    } finally {
      setIsDownloading(false);
    }
  }, [
    canWriteDraft,
    checkpointCurrentWorkingCopy,
    documentGuid,
    draftDocument,
    draftEditingEnabled,
    isLiveEditingView,
    refreshAfterDraftMoved,
    resolvedVersionNumber,
    slug,
  ]);

  const handleRecordVersion = React.useCallback(async () => {
    if (recordVersionInFlightRef.current) return;
    if (!draftDocument || !latestVersionNumber) {
      toast.error('Draft document is missing a version to record');
      return;
    }
    if (!canWriteDraft) {
      toast.error('You only have read access to this draft');
      return;
    }
    if (!isLiveEditingView) {
      toast.error('Open live editing before recording a version');
      return;
    }
    if (activeLiveDraftEnabled && !liveDraftExportReady) {
      toast.error(liveDraftWaitMessage);
      return;
    }
    const editor = editorRef.current;
    if (!editor) {
      toast.error('Draft editor is not ready yet');
      return;
    }

    recordVersionInFlightRef.current = true;
    setIsRecordingVersion(true);
    clearAutosaveTimeout();
    autosaveQueuedAfterCheckpointRef.current = false;
    try {
      if (checkpointPromiseRef.current) {
        await checkpointPromiseRef.current.catch(() => null);
      }
      const savedBuffer = await saveEditorBuffer(editor);
      if (!savedBuffer) {
        throw new Error('Draft editor did not return a saved document');
      }

      const recordedDraft = await saveDraftDocumentDocx(
        slug,
        documentGuid,
        latestVersionNumber,
        savedBuffer,
      );
      const recordedVersionNumber = recordedDraft.latest_version?.version_number ?? latestVersionNumber + 1;

      latestCheckpointGuidRef.current = null;
      setEditorDirty(false);
      await Promise.all([
        mutateDraftDocument(recordedDraft, { revalidate: false }),
        mutateVersions(),
        mutateSelectedVersion(),
        mutateDocx(),
        mutateDraftDocumentsCache(draftDocumentsListCacheKey),
      ]);
      setSelectedVersionNumber(null);
      setViewMode('docx');
      void navigate({
        to: '/matter/$slug/drafting/$documentGuid',
        params: { slug, documentGuid },
        search: {},
        replace: true,
      });
      toast.success(`Created version ${recordedVersionNumber}`);
    } catch (error) {
      const movedToVersion = draftMovedToVersionNumber(error);
      if (movedToVersion) {
        await refreshAfterDraftMoved(movedToVersion);
        return;
      }
      toast.error(apiErrorMessage(error) || 'Could not record draft version');
    } finally {
      recordVersionInFlightRef.current = false;
      setIsRecordingVersion(false);
    }
  }, [
    canWriteDraft,
    clearAutosaveTimeout,
    documentGuid,
    draftDocumentsListCacheKey,
    draftDocument,
    isLiveEditingView,
    latestVersionNumber,
    liveDraftExportReady,
    activeLiveDraftEnabled,
    liveDraftWaitMessage,
    mutateDocx,
    mutateDraftDocument,
    mutateDraftDocumentsCache,
    mutateSelectedVersion,
    mutateVersions,
    navigate,
    refreshAfterDraftMoved,
    setEditorDirty,
    slug,
  ]);

  const handleOpenSourceChat = React.useCallback(() => {
    const chatGuid = draftDocument?.created_from_chat?.chat_guid;
    if (!chatGuid) return;
    openChatInCasePartner(chatGuid);
  }, [draftDocument?.created_from_chat?.chat_guid, openChatInCasePartner]);

  const handleSelectVersion = React.useCallback((versionNumber: number) => {
    setSelectedVersionNumber(versionNumber);
    setViewMode('docx');
    void navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: { slug, documentGuid },
      search: { version: String(versionNumber) },
      replace: true,
    });
  }, [documentGuid, navigate, slug]);

  const handleShowDiff = React.useCallback((versionNumber: number) => {
    setSelectedVersionNumber(versionNumber);
    setViewMode('diff');
    void navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: { slug, documentGuid },
      search: {
        version: String(versionNumber),
        mode: 'diff',
      },
      replace: true,
    });
  }, [documentGuid, navigate, slug]);

  const handleRestoreVersion = React.useCallback(async (versionNumber: number) => {
    if (!draftDocument || !latestVersionNumber) return;
    if (!canWriteDraft) {
      toast.error('You only have read access to this draft');
      return;
    }
    if (versionNumber === latestVersionNumber) {
      toast.error('This version is already current');
      return;
    }

    const confirmed = window.confirm(
      `Restore version ${versionNumber} as the latest version?\n\nThis keeps the full version record and creates a new version from version ${versionNumber}. The current live draft will be replaced.`,
    );
    if (!confirmed) return;

    setRestoringVersionNumber(versionNumber);
    clearAutosaveTimeout();
    autosaveQueuedAfterCheckpointRef.current = false;
    try {
      if (checkpointPromiseRef.current) {
        await checkpointPromiseRef.current.catch(() => null);
      }
      const response = await restoreDraftDocumentVersion(
        slug,
        documentGuid,
        versionNumber,
      );
      latestCheckpointGuidRef.current = null;
      setEditorDirty(false);
      await Promise.all([
        mutateDraftDocument(response.document, { revalidate: true }),
        mutateVersions(),
        mutateSelectedVersion(),
        mutateDocx(),
        mutateDraftDocumentsCache(draftDocumentsListCacheKey),
      ]);
      setSelectedVersionNumber(null);
      setViewMode('docx');
      void navigate({
        to: '/matter/$slug/drafting/$documentGuid',
        params: { slug, documentGuid },
        search: {},
        replace: true,
      });
      toast.success(`Restored version ${versionNumber}`);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Could not restore draft version');
    } finally {
      setRestoringVersionNumber(null);
    }
  }, [
    canWriteDraft,
    clearAutosaveTimeout,
    documentGuid,
    draftDocumentsListCacheKey,
    draftDocument,
    latestVersionNumber,
    mutateDocx,
    mutateDraftDocument,
    mutateDraftDocumentsCache,
    mutateSelectedVersion,
    mutateVersions,
    navigate,
    setEditorDirty,
    slug,
  ]);

  const handleBackToLiveEditing = React.useCallback(() => {
    setSelectedVersionNumber(null);
    setViewMode('docx');
    void navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: { slug, documentGuid },
      search: {},
      replace: true,
    });
  }, [documentGuid, navigate, slug]);

  const recordVersionDisabledReason = React.useMemo(() => {
    if (!canWriteDraft) return 'You only have read access to this draft.';
    if (isRecordingVersion) return 'Creating version.';
    if (!isLiveEditingView) return 'Open live editing before creating a version.';
    if (activeLiveDraftEnabled && !liveDraftExportReady) {
      return liveDraftWaitMessage;
    }
    return null;
  }, [
    canWriteDraft,
    isLiveEditingView,
    isRecordingVersion,
    liveDraftExportReady,
    activeLiveDraftEnabled,
    liveDraftWaitMessage,
  ]);

  const historyTab = React.useMemo(() => ({
    id: DRAFT_HISTORY_TAB_ID,
    label: 'Versions',
    icon: History,
    content: (
      <DraftHistoryPanel
        canRecordVersion={!recordVersionDisabledReason}
        createdFromChat={draftDocument?.created_from_chat}
        dateLocale={dateLocale}
        isLoading={isVersionsLoading}
        isLiveSelected={isLiveEditingView}
        isRecordingVersion={isRecordingVersion}
        isRestoreDisabled={!canWriteDraft}
        latestVersionNumber={latestVersionNumber}
        liveEditedAt={displayedLiveEditedAt}
        onOpenSourceChat={handleOpenSourceChat}
        onRecordVersion={handleRecordVersion}
        onSelectLive={handleBackToLiveEditing}
        onSelectVersion={handleSelectVersion}
        onShowDiff={handleShowDiff}
        onRestoreVersion={handleRestoreVersion}
        recordVersionDisabledReason={recordVersionDisabledReason}
        restoringVersionNumber={restoringVersionNumber}
        selectedVersionNumber={displayVersionNumber}
        versions={versions}
      />
    ),
  }), [
    displayVersionNumber,
    displayedLiveEditedAt,
    draftDocument?.created_from_chat,
    handleOpenSourceChat,
    handleRecordVersion,
    handleBackToLiveEditing,
    handleRestoreVersion,
    handleSelectVersion,
    handleShowDiff,
    isLiveEditingView,
    isRecordingVersion,
    isVersionsLoading,
    latestVersionNumber,
    recordVersionDisabledReason,
    restoringVersionNumber,
    versions,
    canWriteDraft,
    dateLocale,
  ]);

  React.useEffect(() => {
    if (!isSidePanelOpen) return;
    const currentHistoryTab = sidePanelTabs.find((tab) => tab.id === DRAFT_HISTORY_TAB_ID);
    if (!currentHistoryTab || currentHistoryTab === historyTab) return;

    setTabs(sidePanelTabs.map((tab) => tab.id === DRAFT_HISTORY_TAB_ID ? historyTab : tab));
  }, [historyTab, isSidePanelOpen, setTabs, sidePanelTabs]);

  const handleOpenHistory = React.useCallback(() => {
    openPanel([historyTab], DRAFT_HISTORY_TAB_ID);
  }, [historyTab, openPanel]);

  const breadcrumbSubheader = React.useMemo(() => (
    <span className="flex items-center text-gray-500">
      <Link to="/matter/$slug/drafting" params={{ slug }} className="font-medium hover:text-gray-700">Drafting</Link>
      <span className="flex min-w-0 items-center">
        <ChevronRight className="mx-1 h-4 w-4 shrink-0" />
        <span className="min-w-0 truncate font-medium hover:text-gray-700">
          {draftDocument?.title ?? 'Draft document'}
        </span>
      </span>
    </span>
  ), [draftDocument?.title, slug]);

  const hasDraftDocument = Boolean(draftDocument);
  const draftTitleHeader = React.useMemo(() => (
    <DraftTitleEditor
      title={draftDocument?.title ?? 'Draft document'}
      disabled={!hasDraftDocument || !canWriteDraft}
      onSave={handleRenameDraft}
    />
  ), [canWriteDraft, draftDocument?.title, handleRenameDraft, hasDraftDocument]);

  useShellHeader({
    header: draftTitleHeader,
    subheader: breadcrumbSubheader,
  });

  if (!draftDocument && (isLoading || isDraftDocumentValidating || draftDocumentErrorInGrace)) {
    return (
      <div className="flex h-56 items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!draftDocument && isError) {
    return (
      <div className="mx-auto w-full max-w-(--breakpoint-xl) pt-12 pl-2 pr-4">
        <PageError message={apiErrorMessage(isError) || 'Failed to load draft document'} />
      </div>
    );
  }

  if (!draftDocument) {
    return null;
  }

  const displayedVersion = versions.find((version) => version.version_number === displayVersionNumber) ?? null;
  const displayedVersionRecordedAt = selectedVersion?.created_at ?? displayedVersion?.created_at ?? draftDocument.created_at;
  const displayedTimestamp = isLiveEditingView ? displayedLiveEditedAt : displayedVersionRecordedAt;
  const hasLocalCollaborator = draftCollaboration.users.some((collaborator) => collaborator.isLocal);
  const liveCollaborators = uniqueDraftCollaborators([
    ...(activeLiveDraftEnabled && !hasLocalCollaborator
      ? [{
          clientId: -1,
          color: collaborationUser.color,
          email: collaborationUser.email,
          isLocal: true,
          name: collaborationUser.name,
        }]
      : []),
    ...draftCollaboration.users,
  ]);
  const canShowLiveCollaborators = !liveDraftConnectionError
    && draftCollaboration.status === 'connected'
    && draftCollaboration.ready;
  const liveStatusLabel = liveDraftConnectionError
    ? 'Live · offline'
    : !draftCollaboration.ready
    ? 'Joining live session'
    : draftCollaboration.status !== 'connected'
      ? 'Live · reconnecting'
      : 'Live';
  const canShowInlineLiveStatus = activeLiveDraftEnabled && isLiveEditingView;
  const versionStatusLabel = isLiveEditingView ? 'Latest version' : `Version ${displayVersionNumber}`;
  const timestampLabel = isLiveEditingView
    ? `Autosaved ${formatDateTime(displayedTimestamp, dateLocale)}`
    : formatRecordedTimestampLabel(
      displayedTimestamp,
      displayedVersion?.actor_user_name,
      dateLocale,
    );
  const liveRoomHasReusableEditorDocument = activeLiveDraftEnabled
    && draftCollaboration.roomIsAuthoritative
    && liveEditorLoadedBufferRoomRef.current === activeLiveRoomName;
  const viewerDocxError = liveRoomHasReusableEditorDocument || liveDocumentErrorInGrace
    ? null
    : liveDocumentSource.error;
  const viewerDocxLoading = liveRoomHasReusableEditorDocument
    ? false
    : (
      liveDocumentSource.isLoading
      || (!liveDocumentBuffer && liveDocumentSource.isValidating)
      || liveDocumentErrorInGrace
    );

  return (
    <div className="min-h-full bg-slate-50/40 px-4 py-6">
      <main className="mx-auto w-full max-w-[1540px] min-w-0">
        <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
          <DraftDetailToolbar
            canShowInlineLiveStatus={canShowInlineLiveStatus}
            canShowLiveCollaborators={canShowLiveCollaborators}
            canUpdateVisibility={Boolean(draftDocument.can_update_visibility)}
            downloadVersionNumber={isLiveEditingView ? null : displayVersionNumber}
            isDownloading={isDownloading}
            isLiveEditingView={isLiveEditingView}
            isUpdatingVisibility={isUpdatingVisibility}
            liveCollaborators={liveCollaborators}
            liveStatusLabel={liveStatusLabel}
            onBackToLiveEditing={handleBackToLiveEditing}
            onDownload={handleDownload}
            onOpenHistory={handleOpenHistory}
            onVisibilityChange={handleVisibilityChange}
            timestampLabel={timestampLabel}
            versionStatusLabel={versionStatusLabel}
            visibility={draftDocument.visibility}
          />

          <div className="min-w-0 bg-slate-50">
            {effectiveViewMode === 'docx' ? (
              <div className="grid min-w-0 grid-cols-1">
                <div className="relative min-w-0">
                  <DraftDocxViewer
                    key={documentGuid}
                    buffer={liveDocumentBuffer}
                    bufferReloadKey={liveEditorReloadKey}
                    documentId={documentGuid}
                    editorInstanceKey={editorInstanceKey}
                    editorRef={editorRef}
                    error={viewerDocxError}
                    collaboration={activeLiveDraftEnabled ? draftCollaboration.editorCollaboration : null}
                    // Writable sessions stay in SuperDoc editing mode even while
                    // the live room is still connecting. Viewing mode disables the
                    // toolbar's undo stack and does not always recover after join.
                    isEditing={draftEditingEnabled}
                    isLoading={viewerDocxLoading}
                    onChange={handleEditorChange}
                    showPageIndicator
                    stripCrimsonNodeAnchors
                    title={draftDocument.title}
                    versionKey={activeLiveDraftEnabled ? `live-${draftCollaboration.roomName ?? 'pending'}` : displayVersionNumber}
                  />
                </div>
              </div>
            ) : effectiveViewMode === 'diff' ? (
              <DraftVersionDiff
                afterVersion={selectedVersion}
                beforeVersion={previousVersion}
                buffer={changesDocumentBuffer}
                documentGuid={documentGuid}
                error={selectedVersionError || previousVersionError || changesDocxError}
                isLoading={isSelectedVersionLoading || isPreviousVersionLoading || isChangesDocxLoading}
              />
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DraftDetailPage;
